/*----------------------------------
- HOME: FUNNEL
----------------------------------*/

/**
 * "We monitor every one-word domain" (`section#how`): the funnel part fed with the server's figures, formatted by the
 * landing formatter, and the catalog's source kinds and checks.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule Every figure is a server field (`totalDataPoints` from the scanner's snapshot, `totalDomains`,
 *       `checkedLast24h`), never computed here; a figure the server sent as 0 leaves its stage without a figure or
 *       sentence (the part draws them unconditionally, so the page passes empty strings), never a zero.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Funnel, type FunnelFigure } from '@uniquedomains/components/marketing/funnel/Funnel';

import Icon from '@/client/components/ui/icon/Icon';
import {
    HOME_FUNNEL_CHECKS,
    HOME_FUNNEL_HEADLINE,
    HOME_FUNNEL_SAMPLE_LABEL,
    HOME_FUNNEL_SOURCES,
    HOME_FUNNEL_TEXT,
    buildHomeFunnelChecksLabel,
} from '@/client/catalogs/landing/home/funnel';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';
import type { LandingStats } from '@/client/pages/landing/types';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeFunnelProps = {
    stats: Pick<LandingStats, 'totalDataPoints' | 'totalDomains' | 'checkedLast24h'>;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SOURCES = HOME_FUNNEL_SOURCES.map((source) => ({ label: source.label, seed: source.seed, icon: <Icon name={source.icon} size={16} /> }));
const CHECKS = HOME_FUNNEL_CHECKS.map((check) => ({ label: check.label, icon: <Icon name={check.icon} size={15} /> }));
const CHECKS_LABEL = buildHomeFunnelChecksLabel(HOME_FUNNEL_CHECKS);

/** A stage without its figure: no number and no sentence that would hang on nothing. */
const NO_FIGURE: FunnelFigure = { value: '', text: '' };

/*----------------------------------
- HELPERS
----------------------------------*/

const figureOf = (value: number, text: string): FunnelFigure => (value > 0 ? { value: formatCompactStat(value), text } : NO_FIGURE);

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeFunnel = ({ stats }: HomeFunnelProps) => (
    <section class="sec" id="how">
        <div class="wrap fn-head rv">
            <h2>{HOME_FUNNEL_HEADLINE}</h2>
        </div>
        <Funnel
            sources={SOURCES}
            checks={CHECKS}
            checksLabel={CHECKS_LABEL}
            figures={{
                sources: figureOf(stats.totalDataPoints, HOME_FUNNEL_TEXT.sources),
                kept: figureOf(stats.totalDomains, HOME_FUNNEL_TEXT.kept),
                checked: figureOf(stats.checkedLast24h, HOME_FUNNEL_TEXT.checked),
                // A sample, marked as one in the catalog: not a server field.
                sample: { value: HOME_FUNNEL_SAMPLE_LABEL, text: HOME_FUNNEL_TEXT.sample },
            }}
        />
    </section>
);

export default HomeFunnel;
